import React, { useMemo } from "react";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Input,
} from "@elaniintc/goes-design-system";
import PhoneField from "@/components/common/phone-field";
import InfoMessage from "./InfoMessage";
import {
  DropdownList,
  type DropdownListGroup,
} from "@/components/ui/dropdown-list/dropdown-list";
import { Renderify } from "renderify-js";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { nodeVariableToGroups, type Variable } from "../../types";
import type { VariableType } from "../../calculated-field/CalculatedFieldOperation/types";

export type InteroperabilityVariable = {
  field_key_value?: string | number;
  field_name?: string;
  name?: string;
  key_name?: string;
  type?: VariableType;
};

interface DefaultValueFieldProps {
  // Control del tipo de valor (inp, var, cam)
  valueType: "inp" | "var" | "cam";
  onValueTypeChange: (type: "inp" | "var" | "cam") => void;

  // Para modo "inp" (input directo)
  inputValue: string;
  onInputChange: (value: string) => void;
  inputPlaceholder?: string;
  inputType?: "text" | "email" | "phone" | "number" | "date";

  // Validación custom para el input (opcional)
  onInputValidate?: (value: string) => boolean;

  // Para modo "var" (variable)
  selectedVariable: Variable | null;
  onVariableChange: (variable: Variable | null) => void;
  availableVariables: Variable[];

  // Props adicionales para phone field
  isPhoneField?: boolean;

  // Props adicionales para number field
  isNumberField?: boolean;
  numberPlaceholder?: string;

  // Configuración de maxLength para text/email
  maxLength?: number;

  // Mensaje de información personalizado
  infoMessage?: string;

  // Mensaje de error (opcional)
  errorMessage?: string;
  interoperabilityVariables?: InteroperabilityVariable[];

  // Tipo de campo para filtrar variables compatibles
  fieldType?:
    | "text"
    | "email"
    | "number"
    | "select"
    | "multiselect"
    | "textarea"
    | "checkbox"
    | "radio"
    | "file"
    | "date"
    | "datetime"
    | "time"
    | "date_range"
    | "datetime_range"
    | "identification"
    | "phone"
    | "interoperability"
    | "switch";

  // Para componentes personalizados (ej: DateInput, TimeInput)
  children?: React.ReactNode;
}

const getCompatibleVariableTypes = (fieldType: string): string[] => {
  if (!fieldType) return []; // Sin filtro si no se especifica tipo

  switch (fieldType) {
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "identification":
      return ["string"];

    case "number":
      return ["number", "float"];

    case "date":
    case "datetime":
    case "time":
    case "date_range":
    case "datetime_range":
      return ["date"];

    case "checkbox":
    case "switch":
      return ["boolean"];

    case "file":
      return ["file"];

    case "select":
    case "multiselect":
      return ["string", "number", "array"];

    case "interoperability":
      return []; // No filtrar para interoperabilidad

    default:
      return [];
  }
};

const DefaultValueField: React.FC<DefaultValueFieldProps> = ({
  valueType,
  onValueTypeChange,
  inputValue,
  onInputChange,
  inputPlaceholder = "Escribe un valor por defecto",
  inputType = "text",
  onInputValidate,
  selectedVariable,
  onVariableChange,
  // availableVariables,
  isPhoneField = false,
  isNumberField = false,
  numberPlaceholder,
  maxLength,
  infoMessage = "Si está vacío no se aplicará ningún valor por defecto",
  errorMessage,
  interoperabilityVariables = [],
  fieldType,
  children,
}) => {
  // Función para obtener tipos de variables compatibles según el tipo de campo

  const handleToggle = () => {
    // Ciclo: inp -> var -> cam -> inp
    if (valueType === "inp") {
      onValueTypeChange("var");
      onVariableChange(null); // Resetear variable al cambiar a var
    } else if (valueType === "var") {
      onVariableChange(null); // Resetear variable al cambiar a cam
      onValueTypeChange("cam");
    } else {
      onVariableChange(null); // Resetear variable al cambiar a inp
      onValueTypeChange("inp");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Validación personalizada si existe
    if (onInputValidate && !onInputValidate(value)) {
      return;
    }

    // Validación de maxLength para text/email
    if (maxLength && !isNaN(maxLength)) {
      if (value.length <= maxLength) {
        onInputChange(value);
      }
      return;
    }

    onInputChange(value);
  };

  const typeToIcon = (type: string) => {
    switch (type) {
      case "string":
        return "text";
      case "int":
      case "float":
      case "number":
        return "hashtag";
      case "boolean":
        return "toggle-on";
      case "date":
        return "calendar";
      case "array":
        return "list";
      case "file":
        return "file";
      default:
        return "text";
    }
  };

  const convertInteroperabilityToVariables = (
    interopVars: InteroperabilityVariable[],
  ) => {
    // No filtrar variables de interoperabilidad por tipo
    // El filtrado solo aplica para node variables (var mode)

    // Agrupar por field_name
    const grouped = interopVars.reduce(
      (acc, curr) => {
        const groupName = curr.field_name || "Otros";
        if (!acc[groupName]) {
          acc[groupName] = [];
        }
        acc[groupName].push({
          value: `${curr.field_key_value?.toString()}_${curr.name}`,
          label: curr.name || "",
          detail: curr.type || "",
          icon: {
            type: "far",
            name: typeToIcon(curr.type || "text"),
            size: "md",
            className: "text-neutral-quiet",
          },
        });
        return acc;
      },
      {} as Record<
        string,
        {
          value: string;
          label: string;
          detail: string;
          icon: { type: string; name: string; size: string; className: string };
        }[]
      >,
    );

    const groups = Object.entries(grouped).map(([groupName, items]) => ({
      groupId: groupName,
      groupLabels: [groupName],
      items,
      icon: {
        type: "far",
        name: "diagram-nested",
        size: "sm",
        className: "text-action-quiet",
      },
    }));

    const defaultGroup = {
      groupId: "__default__",
      groupLabels: ["Vacío"],
      items: [{ value: "__none__", label: "Selecciona una variable" }],
    };

    const result = [defaultGroup, ...groups];
    console.log(
      "convertInteroperabilityToVariables result:",
      interoperabilityVariables,
    );
    return result;
  };

  const { variables: nodeVariables } = useGetVariables();
  const [variableSearch, setVariableSearch] = React.useState("");

  const variables = useMemo(() => {
    const getNodeVariableGroups = (): DropdownListGroup[] => {
      if (!nodeVariables || nodeVariables.length === 0) {
        return [
          {
            groupId: "__default__",
            groupLabels: ["Vacío"],
            items: [{ value: "__none__", label: "Selecciona una variable" }],
          },
        ];
      }

      const compatibleTypes = getCompatibleVariableTypes(fieldType || "");

      // Filtrar nodos y sus variables por tipo compatible
      const filteredNodes = nodeVariables
        .map((node) => ({
          ...node,
          variables:
            compatibleTypes.length > 0
              ? node.variables.filter(
                  (v) => v.type && compatibleTypes.includes(v.type),
                )
              : node.variables,
        }))
        .filter((node) => node.variables.length > 0); // Solo nodos con variables compatibles

      if (filteredNodes.length === 0) {
        return [
          {
            groupId: "__default__",
            groupLabels: ["Vacío"],
            items: [
              { value: "__none__", label: "No hay variables compatibles" },
            ],
          },
        ];
      }

      const groups = nodeVariableToGroups(filteredNodes);
      const defaultGroup: DropdownListGroup = {
        groupId: "__default__",
        groupLabels: ["Vacío"],
        items: [{ value: "__none__", label: "Selecciona una variable" }],
      };

      return [
        defaultGroup,
        ...groups
          .filter((g) => {
            if (variableSearch) {
              return g.items.some((item) => {
                if (typeof item.label === "string") {
                  return item.label
                    .toLowerCase()
                    .includes(variableSearch.toLowerCase());
                }
                return true;
              });
            }
            return true;
          })
          .filter((g) => g.items.length > 0),
      ];
    };
    return getNodeVariableGroups();
  }, [nodeVariables, variableSearch, fieldType]);

  // Convertir NodeVariable[] a grupos para DropdownList

  // Helper para encontrar una variable por ID en todos los nodos
  const findVariableById = (variableId: string) => {
    for (const node of nodeVariables) {
      const variable = node.variables.find((v) => v.id === variableId);
      if (variable) {
        return {
          variable,
          nodeId: node.nodeId,
        };
      }
    }
    return null;
  };

  return (
    <>
      <GroupTextField label="Valor por defecto" labelHelperText="Opcional">
        <GroupTextFieldRow className="items-center">
          <Button
            variant="quiet"
            group="left"
            onClick={handleToggle}
            className="font-semibold"
          >
            {valueType}
          </Button>

          <Renderify condition={valueType === "inp"}>
            {children ? (
              <div className="flex-1 rounded rounded-r-2xl">{children}</div>
            ) : (
              <>
                <Renderify condition={isPhoneField}>
                  <div className="w-full">
                    <PhoneField
                      label=""
                      value={inputValue}
                      onChange={handleInputChange}
                      placeholder={inputPlaceholder}
                      className="w-full rounded-tl-none rounded-bl-none"
                    />
                  </div>
                </Renderify>
                <Renderify condition={!isPhoneField}>
                  <Input
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder={
                      isNumberField ? numberPlaceholder : inputPlaceholder
                    }
                    className="w-full rounded-tl-none rounded-bl-none"
                    type={inputType}
                    {...(isNumberField && { min: 0 })}
                  />
                </Renderify>
              </>
            )}
          </Renderify>

          <Renderify condition={valueType === "cam"}>
            <div className="flex w-full">
              <DropdownList
                options={convertInteroperabilityToVariables(
                  interoperabilityVariables,
                )}
                selectedValue={{
                  value: selectedVariable?.id || "__none__",
                  label: selectedVariable?.name || "Selecciona una variable",
                  icon: selectedVariable
                    ? {
                        type: "far",
                        name: "diagram-nested",
                        size: "sm",
                        className: "text-action-quiet",
                      }
                    : undefined,
                }}
                toggleSelection={(item) => {
                  if (item.value === "__none__") {
                    onVariableChange(null);
                  } else {
                    const found = interoperabilityVariables.find(
                      (v) =>
                        `${v.field_key_value?.toString()}_${v.name}` ===
                        item.value,
                    );
                    if (found) {
                      onVariableChange({
                        id: `${found.field_key_value?.toString()}_$key$_${found.name}_$keyname$_${found.key_name || ""}`,
                        name: found.name || "",
                        type: found.type || "",
                      });
                    }
                  }
                }}
                widthClassName="flex-1"
                group="right"
              />
            </div>
          </Renderify>

          <Renderify condition={valueType === "var"}>
            <div className="flex w-full">
              <DropdownList
                search={{
                  placeholder: "Buscar variable...",
                  query: variableSearch,
                  onSearch: setVariableSearch,
                }}
                options={variables}
                selectedValue={{
                  value: selectedVariable?.id || "__none__",
                  label: selectedVariable?.name || "Selecciona una variable",
                  icon: selectedVariable
                    ? {
                        type: "far",
                        name: typeToIcon(selectedVariable.type || "text"),
                        size: "sm",
                        className: "text-action-quiet",
                      }
                    : undefined,
                }}
                toggleSelection={(item) => {
                  if (item.value === "__none__") {
                    onVariableChange(null);
                  } else {
                    const result = findVariableById(item.value);
                    if (result) {
                      onVariableChange(result.variable);
                    }
                  }
                }}
                widthClassName="flex-1"
                group="right"
              />
            </div>
          </Renderify>
        </GroupTextFieldRow>
      </GroupTextField>

      {errorMessage && (
        <InfoMessage className="text-danger">{errorMessage}</InfoMessage>
      )}

      {!errorMessage && infoMessage && <InfoMessage>{infoMessage}</InfoMessage>}
    </>
  );
};

export default DefaultValueField;
